import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { AdminPageHeader, DataTable } from "../../components/admin/DataTable";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

// #136 Befund 1: Status deutsch statt roh englisch; Default-Ansicht "aktiv"
// (pending/claimed/ordered wie die Leitstand-KPI), Erledigtes verschwindet.
const STATUS_LABELS = {
    pending: "offen",
    claimed: "übernommen",
    ordered: "bestellt",
    fulfilled: "erledigt",
    cancelled: "storniert",
};
const ACTIVE_STATUSES = ["pending", "claimed", "ordered"];
const STATUS_FILTERS = [
    { v: "active", l: "aktive (offen/übernommen/bestellt)" },
    { v: "pending", l: "nur offene" },
    { v: "ordered", l: "nur bestellte" },
    { v: "fulfilled", l: "erledigte" },
    { v: "cancelled", l: "stornierte" },
    { v: "all", l: "alle" },
];

export default function AdminNachbestellung() {
    const [tasks, setTasks] = useState([]);
    const [articles, setArticles] = useState({});
    const [positions, setPositions] = useState({});
    const [selected, setSelected] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();
    const statusFilter = searchParams.get("status") || "active";
    const visibleTasks =
        statusFilter === "all"
            ? tasks
            : statusFilter === "active"
                ? tasks.filter((t) => ACTIVE_STATUSES.includes(t.status))
                : tasks.filter((t) => t.status === statusFilter);
    const load = () =>
        Promise.all([
            rpc.get("/restock"),
            rpc.get("/master/articles"),
            rpc.get("/master/stock-positions"),
        ]).then(([t, a, p]) => {
            if (t.ok) setTasks(t.data);
            if (a.ok) setArticles(Object.fromEntries(a.data.map((x) => [x.id, x])));
            if (p.ok) setPositions(Object.fromEntries(p.data.map((x) => [x.id, x])));
        });
    useEffect(() => { load(); }, []);

    const generate = async () => {
        const res = await rpc.post("/restock/generate", {});
        if (!res.ok) return toast.error(res.error);
        toast.success(`${res.data.created} Task(s) angelegt.`);
        load();
    };
    const bulk = async (status) => {
        // Nur sichtbare Zeilen wirken -- eine Auswahl darf einen Filterwechsel
        // nicht unsichtbar überleben.
        const visibleIds = new Set(visibleTasks.map((t) => t.id));
        const ids = Object.keys(selected).filter((k) => selected[k] && visibleIds.has(k));
        if (!ids.length) return toast.error("Keine Zeilen ausgewählt.");
        const res = await rpc.post("/restock/bulk-status", { ids, status });
        if (!res.ok) return toast.error(res.error);
        toast.success(`${res.data.updated} aktualisiert.`);
        setSelected({});
        load();
    };

    const columns = [
        {
            key: "select",
            label: "",
            sortable: false,
            render: (r) => (
                <Checkbox
                    checked={!!selected[r.id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [r.id]: !!v }))}
                />
            ),
        },
        { key: "status", label: "Status", render: (r) => STATUS_LABELS[r.status] || r.status },
        {
            key: "article",
            label: "Artikel",
            sortable: false,
            render: (r) => {
                const p = positions[r.stock_position_id];
                return articles[p?.article_id]?.name || "—";
            },
        },
        {
            key: "qty",
            label: "Bestand",
            sortable: false,
            render: (r) => {
                const p = positions[r.stock_position_id];
                return p ? `${p.quantity} / Min ${p.min_quantity ?? "—"}` : "—";
            },
        },
        {
            key: "created_at",
            label: "Seit",
            render: (r) => new Date(r.created_at).toLocaleDateString("de-DE"),
        },
    ];

    return (
        <>
            <AdminPageHeader title="Nachbestellungen" subtitle={`${visibleTasks.length} von ${tasks.length} Task(s)`}>
                <Button onClick={generate} data-testid="restock-generate">
                    <RefreshCw className="mr-1 h-4 w-4" /> Auto-Generieren
                </Button>
                <Button variant="outline" onClick={() => bulk("ordered")} data-testid="restock-bulk-ordered">
                    Ausgewählte → bestellt
                </Button>
                <Button variant="outline" onClick={() => bulk("fulfilled")} data-testid="restock-bulk-fulfilled">
                    Ausgewählte → erledigt
                </Button>
            </AdminPageHeader>
            <DataTable
                testId="admin-restock-table"
                columns={columns}
                rows={visibleTasks}
                rowKey={(r) => r.id}
                filtersSlot={
                    <Select
                        value={statusFilter}
                        onValueChange={(v) =>
                            setSearchParams((prev) => {
                                const next = new URLSearchParams(prev);
                                if (v === "active") next.delete("status");
                                else next.set("status", v);
                                next.delete("p");
                                return next;
                            }, { replace: true })
                        }
                    >
                        <SelectTrigger className="h-10 w-64" data-testid="restock-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_FILTERS.map((s) => (
                                <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            />
        </>
    );
}
